/**
 * The `currency-tabs` control — the wallet's تومان/تتر pair, the dashboard's
 * chart ranges, and the buy/sell switch on the quick-trade widget.
 *
 * Distinct from `TabSelector` (§5.5), which is 52 high with 4px padding and a
 * gap. This one has no padding or gap: the segments butt up against the
 * container's inner edge, and the active one is a radius-12 primary fill.
 */
export interface SegmentedItem<T extends string> {
  value: T
  label: string
}

interface SegmentedTabsProps<T extends string> {
  items: readonly SegmentedItem<T>[]
  value: T
  onChange: (value: T) => void
  /** `equal` splits the width; `auto` sizes each segment to its label. */
  width?: 'equal' | 'auto'
  size?: 'md' | 'sm'
  'aria-label'?: string
  className?: string
}

export function SegmentedTabs<T extends string>({
  items,
  value,
  onChange,
  width = 'equal',
  size = 'md',
  'aria-label': ariaLabel,
  className = '',
}: SegmentedTabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`flex shrink-0 rounded-14 border border-line-8 bg-surface-3 ${
        size === 'md' ? 'h-[44px]' : 'h-10'
      } ${className}`}
    >
      {items.map((item) => {
        const active = item.value === value
        return (
          <button
            key={item.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={[
              'sx-tight rounded-12 transition-colors duration-(--sx-dur)',
              width === 'equal' ? 'flex-1' : 'px-3',
              size === 'md' ? 'text-14' : 'text-12',
              active ? 'bg-primary font-semibold text-ink' : 'font-normal text-ink',
            ].join(' ')}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
